import { useCallback, useEffect, useRef, useState } from 'react';

import { useDraftContractEmailMutation } from 'core/graphql';

import { noteSalesAiError, resetSalesAiOff } from '../Crm/salesAiOff';
import { newSmsRequestKey } from '../Sms/smsModel';
import { ContractEmailAiKind } from './contractWindowModel';

/**
 * ТЗ 0120, этап 2 — ИИ-письмо подписанту договора (приглашение,
 * напоминание, срок истекает, follow-up, после подписи). Письмо всегда
 * по-польски, `{link}` остаётся заглушкой — его подставит отправка
 * (`sendContractEmail` / `sendContract`). Отправляет только человек: из
 * «Переписки» с `aiDraftId` или из окна приглашения.
 */

/** Ответ пришёл после закрытия окна или смены договора — молча выбросить. */
export const STALE_DRAFT = 'stale';

export interface IContractEmailAiDraft {
  id: string;
  kind: ContractEmailAiKind;
  subject: string;
  html: string;
  /** Текст без разметки: тело приглашения (`emailBody`) с `{link}`. */
  text: string;
  cached: boolean;
  /** С каким пожеланием написан — «Сгенерировать заново» повторяет его. */
  hint: string;
}

export interface IContractEmailAiResult {
  draft: IContractEmailAiDraft | null;
  /** Код отказа сервера или `NETWORK`. */
  errorCode: string | null;
  /** Текст сервера (бюджет — сколько осталось и т. п.). */
  errorMessage: string | null;
}

export interface IContractEmailAiRequest {
  kind: ContractEmailAiKind;
  hint?: string;
  /** «Сгенерировать заново» — мимо кеша. */
  regenerate?: boolean;
}

/**
 * Один запрос за раз: клик, пока письмо пишется, отбивается без запроса.
 * `busy` — вид, который сейчас пишется. Ключ запроса — новый на каждый
 * клик (сервер сам вернёт сохранённый черновик по отпечатку контекста).
 */
export const useContractEmailDraft = (contractId: string) => {
  const [mutate] = useDraftContractEmailMutation();
  const [busy, setBusy] = useState<ContractEmailAiKind | null>(null);
  const busyRef = useRef(false);
  // Окно закрыли или открыли другой договор (тело пересоздано по ключу) —
  // ответ, который ещё в пути, уже никому не нужен.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const generate = useCallback(
    async ({
      kind,
      hint = '',
      regenerate = false,
    }: IContractEmailAiRequest): Promise<IContractEmailAiResult> => {
      if (busyRef.current) {
        return { draft: null, errorCode: 'in_progress', errorMessage: null };
      }
      const cleanHint = hint.trim();
      const requestKey = newSmsRequestKey();
      busyRef.current = true;
      setBusy(kind);
      try {
        const { data } = await mutate({
          variables: {
            contractId,
            kind,
            hint: cleanHint || null,
            regenerate,
            requestKey,
          },
        });
        if (!aliveRef.current) {
          return { draft: null, errorCode: STALE_DRAFT, errorMessage: null };
        }
        const payload = data?.draftContractEmail;
        const draft = payload?.draft;
        if (payload?.success && draft) {
          // ТЗ 0128: черновик есть — ИИ включён, запомненный отказ снят.
          resetSalesAiOff();
          return {
            draft: {
              id: draft.id,
              kind,
              subject: draft.subject,
              html: draft.html,
              text: draft.text,
              cached: Boolean(draft.cached),
              hint: cleanHint,
            },
            errorCode: null,
            errorMessage: null,
          };
        }
        const error = payload?.customErrors?.find(Boolean);
        noteSalesAiError(error?.code);
        return {
          draft: null,
          errorCode: error?.code || 'internal_error',
          errorMessage: error?.message || null,
        };
      } catch (caught) {
        return {
          draft: null,
          errorCode: aliveRef.current ? 'NETWORK' : STALE_DRAFT,
          errorMessage: null,
        };
      } finally {
        busyRef.current = false;
        if (aliveRef.current) setBusy(null);
      }
    },
    [contractId, mutate],
  );

  return { generate, busy };
};
